import styled from 'styled-components';
import MediaQuery from 'react-responsive';
import Grid from '#/components/rfq-common/Grid';
import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';
import {
  Drfq,
  getDrfqStatusLabel,
  isTaker,
} from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';

import {
  DrfqDetailsContent,
  ExecutedTime,
  IdsContainer,
  MODAL_TITLES,
  MobileRfqInfo,
  RfqDateInfo,
} from '#/components/UnifiedRfqs/components/DetailsModal';

import { hourFormat } from '@paradigm/utils/src/date';
import {
  useDrfqTradeTapeFetcher,
  useTakerExecutedRfqInfo,
  useTakerOrderbookFetcher,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/hooks';
import { TradeTapeItem } from '@paradigm/features/src/unified-rfqs/entities/drfq/trades';
import { RfqProvider } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import useFetchedDrfq from '@paradigm/features/src/unified-rfqs/contexts/use-fetched-drfq';
import { isDrfqId } from '@paradigm/features/src/unified-rfqs/entities/rfq';
import { findDrfqBaseLegIndex } from '@paradigm/features/src/unified-rfqs/utils/legs';
import { RfqOrder } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { ProductInfo } from '#/components/UnifiedRfqs/components/RfqStrategySummary';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import LoadingModal from '#/components/UnifiedRfqs/components/LoadingModal';
import {
  useContextMenuActions,
  useContextMenuView,
} from '#/components/UnifiedRfqs/context-menu/context-menu';
import { processLeg } from '@paradigm/features/src/unified-rfqs/ui/drfq/legs-table-functions';
import BboViewProvider from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import TradeDescription from '../TradeDescription';
import OrderbookTradeRecap from '../trade-recap/OrderbookTradeRecap';
import TradeLegPricesTable from '../TradeLegPricesTable';
import ClosedDrfqLegsTable from '../ClosedDrfqLegsTable';

const MODAL_WIDTH = 780;

export default function RfqDetailsModal() {
  const { rfqId, isItemDetailsOpen } = useContextMenuView();

  if (!isItemDetailsOpen) return null;

  if (rfqId == null || !isDrfqId(rfqId)) return <NoRfqFoundModalContent />;

  return (
    <RfqProvider rfqId={rfqId} includeDrafts>
      <RfqDetailsModalLoader rfqId={rfqId} />
    </RfqProvider>
  );
}

function RfqDetailsModalLoader({ rfqId }: { rfqId: Drfq['id'] }) {
  const { drfq, isLoading: isLoadingDrfq } = useFetchedDrfq(rfqId);
  const {
    isLoading: isLoadingOrders,
    bids,
    asks,
  } = useTakerOrderbookFetcher(rfqId);
  const { trade, isLoading: isLoadingTrade } = useDrfqTradeTapeFetcher(rfqId);
  const isLoading = isLoadingDrfq || isLoadingOrders || isLoadingTrade;

  if (isLoading) return <LoadingModal />;
  if (drfq == null) return <NoRfqFoundModalContent />;

  return (
    <BboViewProvider>
      <RfqDetailsModalContent
        drfq={drfq}
        trade={trade}
        bids={bids}
        asks={asks}
      />
    </BboViewProvider>
  );
}

interface DetailsModalProps {
  readonly drfq: Drfq;
  readonly trade: TradeTapeItem | null;
  readonly bids: readonly RfqOrder[];
  readonly asks: readonly RfqOrder[];
}

function RfqDetailsModalContent({
  drfq,
  trade,
  bids,
  asks,
}: DetailsModalProps) {
  const { isItemDetailsOpen } = useContextMenuView();
  const { closeDetailsModal } = useContextMenuActions();

  const baseLegIndex = findDrfqBaseLegIndex(drfq.kind, drfq.legs);
  const productCode = drfq.product_codes[baseLegIndex]!;
  const { executedFromDesk, executedOrder } = useTakerExecutedRfqInfo(
    drfq.id,
    trade?.side,
  );
  const isExecuted = trade != null;
  const label = getDrfqStatusLabel(drfq, isExecuted);
  const modalTitle = MODAL_TITLES[label];
  const isMobile = useIsMobile();
  const leftInfo = isMobile ? (
    <RfqDateInfo date={drfq.last_updated_at} />
  ) : (
    <ProductInfo venue={drfq.venue} productCode={productCode} />
  );

  const legsTableInfo =
    trade == null ? drfq.legs.map(processLeg) : trade.legs.map(processLeg);
  return (
    <HeadedModal
      title={modalTitle}
      isOpen={isItemDetailsOpen}
      close={closeDetailsModal}
      leftInfo={leftInfo}
      aria-label="unified-rfq rfq details modal"
    >
      <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
        <MobileRfqInfo
          venue={drfq.venue}
          productCode={productCode}
          description={drfq.description}
        />
      </MediaQuery>
      <Grid cols={1} css="padding: 1rem;">
        <RfqDescription
          drfq={drfq}
          trade={trade}
          counterpartyDesk={executedFromDesk}
        />
        <LegsContainer>
          <ClosedDrfqLegsTable legs={legsTableInfo} />
          {isExecuted && !isMobile && <TradeLegPricesTable trade={trade} />}
        </LegsContainer>
        {isExecuted && isTaker(drfq) && (
          <RfqProvider rfqId={drfq.id}>
            <OrderbookTradeRecap
              executedOrder={executedOrder}
              executionSide={trade.side}
              drfq={drfq}
              bids={bids}
              asks={asks}
            />
          </RfqProvider>
        )}
        <IdsContainer>
          <div aria-label="rfq id">RFQ ID: {drfq.id}</div>
          {isExecuted && <div aria-label="trade id">Trade ID: {trade.id}</div>}
          {executedOrder != null && (
            <div aria-label="order id">Order ID: {executedOrder.id}</div>
          )}
        </IdsContainer>
        <DetailsTime drfq={drfq} trade={trade} />
      </Grid>
    </HeadedModal>
  );
}

const LegsContainer = styled.div`
  display: flex;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

function DetailsTime({
  trade,
  drfq,
}: Pick<DetailsModalProps, 'trade' | 'drfq'>) {
  if (trade != null) {
    return (
      <ExecutedTime>
        <span css="opacity: 0.5">Executed at</span>{' '}
        {hourFormat(trade.executed_at)}
      </ExecutedTime>
    );
  }

  if (drfq.closed_reason === 'CANCELED_BY_CREATOR') {
    return (
      <ExecutedTime>
        <span css="opacity: 0.5">Canceled at</span>{' '}
        {hourFormat(drfq.last_updated_at)}
      </ExecutedTime>
    );
  }

  if (drfq.closed_reason === 'EXPIRED') {
    return (
      <ExecutedTime>
        <span css="opacity: 0.5">Expired at</span>{' '}
        {hourFormat(drfq.last_updated_at)}
      </ExecutedTime>
    );
  }

  return null;
}

function NoRfqFoundModalContent() {
  const { isItemDetailsOpen } = useContextMenuView();
  const { closeDetailsModal } = useContextMenuActions();
  return (
    <HeadedModal
      title="Details"
      isOpen={isItemDetailsOpen}
      close={closeDetailsModal}
      width={MODAL_WIDTH}
    >
      <p css="color: red; margin: 2rem; text-align: center">RFQ not found 😓</p>
    </HeadedModal>
  );
}

function RfqDescription({
  drfq,
  trade,
  counterpartyDesk,
}: {
  drfq: Drfq;
  trade: TradeTapeItem | null;
  counterpartyDesk?: string | undefined | null;
}) {
  return trade != null ? (
    <TradeDescription
      viewerRole={drfq.role}
      trade={trade}
      drfq={drfq}
      counterpartyDesk={counterpartyDesk}
    />
  ) : (
    <MediaQuery minWidth={MOBILE_BREAKPOINT}>
      <DrfqDetailsContent drfq={drfq} />
    </MediaQuery>
  );
}
